/**
 * Cart management - AJAX cart + mini-cart drawer
 * El drawer se refresca vía Shopify Sections API (?sections=cart-drawer)
 * para no descargar/parsear la página completa.
 */
(function() {
  'use strict';

  var miniCart = document.getElementById('mini-cart');
  if (!miniCart) return;

  /* ============ Open/Close Mini Cart ============ */
  var releaseFocusTrap = null;

  function openMiniCart() {
    miniCart.classList.add('is-open');
    miniCart.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    if (window.ThemeUtils && ThemeUtils.trapFocus) {
      releaseFocusTrap = ThemeUtils.trapFocus(miniCart.querySelector('.mini-cart__drawer') || miniCart);
    }
  }

  function closeMiniCart() {
    miniCart.classList.remove('is-open');
    miniCart.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (releaseFocusTrap) {
      releaseFocusTrap();
      releaseFocusTrap = null;
    }
  }

  window.openMiniCart = openMiniCart;
  window.closeMiniCart = closeMiniCart;
  /* Expuesto para que la página /cart (main-cart.liquid) refresque el drawer
     tras sus propios cambios de cantidad. */
  window.refreshMiniCart = function() { return refreshMiniCart(); };

  /* Si la página /cart está abierta, re-renderízala tras un cambio hecho desde
     el mini-cart (main-cart escucha 'cart:refresh'). Sin esto, cambiar la
     cantidad en el drawer no se reflejaba en la lista de /cart. */
  function emitCartChanged() {
    document.dispatchEvent(new CustomEvent('cart:changed'));
  }

  function syncCartPage() {
    emitCartChanged(); // la wishlist re-sincroniza su estado "En el carrito"
    if (document.querySelector('[data-cart-section]')) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }

  /* Close buttons and backdrop */
  miniCart.querySelectorAll('[data-mini-cart-close]').forEach(function(el) {
    el.addEventListener('click', closeMiniCart);
  });

  /* ESC key */
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && miniCart.classList.contains('is-open')) {
      closeMiniCart();
    }
  });

  /* Cart icon click → open mini cart */
  document.querySelectorAll('[data-cart-toggle]').forEach(function(el) {
    el.addEventListener('click', function(e) {
      e.preventDefault();
      openMiniCart();
    });
  });

  /* ============ Mini Cart Interactions ============ */
  /* Guard anti doble-clic (PL-7): un update a la vez, drawer marcado busy */
  var cartBusy = false;

  miniCart.addEventListener('click', function(e) {
    var qtyBtn = e.target.closest('[data-mc-qty]');
    if (qtyBtn) {
      if (cartBusy) return;
      var key = qtyBtn.dataset.lineKey;
      var change = parseInt(qtyBtn.dataset.mcQty);
      var itemEl = qtyBtn.closest('[data-mini-cart-item]');
      var valEl = itemEl.querySelector('[data-mc-qty-val]');
      var current = parseInt(valEl.textContent) || 1;
      var newQty = Math.max(0, current + change);
      if (newQty === 0) {
        /* Quitar la línea sí cambia el conteo → refresh completo del drawer */
        if (itemEl) itemEl.style.opacity = '0.4';
        updateCart(key, 0).catch(function() { if (itemEl) itemEl.style.opacity = ''; });
      } else {
        /* Optimista: el número cambia YA; luego refrescamos SOLO esa línea +
           el footer (no toda la lista) → sin parpadeo. La línea re-renderizada
           trae el precio al día y el + deshabilitado si topa inventario. */
        valEl.textContent = newQty;
        updateCartLine(key, newQty, itemEl, current, valEl);
      }
    }

    var removeBtn = e.target.closest('[data-mc-remove]');
    if (removeBtn) {
      var rmItem = removeBtn.closest('[data-mini-cart-item]');
      if (rmItem) rmItem.style.opacity = '0.4';
      updateCart(removeBtn.dataset.lineKey, 0).catch(function() {
        if (rmItem) rmItem.style.opacity = '';
      });
    }
  });

  /* ============ Cart API ============ */

  /* La nota del pedido solo existe en /cart (main-cart.liquid la registra en
     window). Si hay algo sin guardar se persiste antes de tocar cantidades:
     esos cambios re-renderizan la página y repintarían el textarea con la
     nota vieja. */
  function flushNote() {
    return window.flushCartNote ? window.flushCartNote() : Promise.resolve();
  }

  function updateCart(key, quantity) {
    if (cartBusy) return Promise.resolve();
    cartBusy = true;
    miniCart.classList.add('is-busy');

    return flushNote()
    .then(function() {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
    })
    .then(function(r) {
      if (!r.ok) throw new Error('Cart update failed');
      return r.json();
    })
    .then(function(cart) {
      return refreshMiniCart().then(function() { return cart; });
    })
    .then(function(cart) {
      updateCartBadge(cart.item_count);
      syncPageSteppers(cart); // refleja el cambio en el stepper del PDP
      syncCartPage();         // y en la lista de /cart si está abierta
    })
    .finally(function() {
      cartBusy = false;
      miniCart.classList.remove('is-busy');
    });
  }

  /* Resuelve con la cantidad final de la línea (para que el caller pueda
     pintar un stepper con el número real). */
  window.addToCart = function(variantId, quantity) {
    quantity = quantity || 1;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] })
    })
    .then(function(r) {
      if (!r.ok) {
        return r.json().then(function(errorData) {
          var error = new Error(errorData && errorData.description ? errorData.description : 'Add to cart failed');
          throw error;
        });
      }
      return r.json();
    })
    .then(function(data) {
      var line = data.items ? data.items[0] : data;
      var qty = line && line.quantity ? line.quantity : quantity;
      return refreshMiniCart().then(function(count) {
        if (count !== null) updateCartBadge(count);
        if (window.ThemeUtils && ThemeUtils.announce && miniCart.dataset.tAdded) {
          ThemeUtils.announce(miniCart.dataset.tAdded);
        }
        openMiniCart();
        emitCartChanged(); // wishlist marca "En el carrito"
        return qty;
      });
    });
  };

  /* ===== Quick-add + stepper de cantidad en cards (página de búsqueda) ===== */

  function lineQty(cart, variantId) {
    var id = parseInt(variantId, 10);
    var item = (cart.items || []).filter(function(i) { return i.id === id; })[0];
    return item ? item.quantity : 0;
  }

  /* Sincroniza los steppers de la PÁGINA (PDP, cards) con la cantidad real del
     carrito. Se llama tras cambios en el mini-cart para que el buy box del PDP
     refleje lo mismo. qty 0 → el stepper vuelve al botón "Agregar". */
  function syncPageSteppers(cart) {
    if (!cart || !cart.items) return;
    var byId = {};
    cart.items.forEach(function(it) { byId[it.id] = it.quantity; });
    document.querySelectorAll('[data-card-stepper]').forEach(function(stepper) {
      if (stepper.classList.contains('is-busy')) return;
      var variantId = stepper.getAttribute('data-variant-id');
      var qty = byId[parseInt(variantId, 10)] || 0;
      if (qty === 0) {
        revertStepper(stepper, variantId, stepper.getAttribute('data-add-label'));
        return;
      }
      var valEl = stepper.querySelector('[data-card-qty-val]');
      if (valEl) valEl.textContent = qty;
      var max = stepper.dataset.max != null ? parseInt(stepper.dataset.max, 10) : null;
      var plus = stepper.querySelector('[data-card-qty="1"]');
      if (plus && max != null) plus.disabled = qty >= max;
    });
  }

  // Cambia la cantidad de una variante a un valor absoluto. Devuelve el cart.
  function changeCartQty(variantId, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: String(variantId), quantity: quantity })
    })
      /* Sin chequear r.ok, un 422 (excede stock) devolvía un JSON de error sin
         .items → lineQty()=0 y el contador caía a 0. */
      .then(function(r) { if (!r.ok) throw new Error('change failed'); return r.json(); })
      .then(function(cart) {
        return refreshMiniCart().then(function(count) {
          if (count !== null) updateCartBadge(count);
          return cart;
        });
      });
  }

  /* pdp=true → variante grande para el buy box (mismos handlers delegados).
     max = tope de inventario (null/undefined = sin tope) → deshabilita el + al llegar. */
  function stepperHtml(variantId, qty, addLabel, pdp, max) {
    var hasMax = max != null && !isNaN(max);
    var atMax = hasMax && qty >= max;
    return '<div class="product-card__stepper' + (pdp ? ' product-form__stepper' : '') + '" data-card-stepper'
      + (pdp ? ' data-pdp="1"' : '')
      + (hasMax ? ' data-max="' + max + '"' : '')
      + ' data-variant-id="' + variantId + '"'
      + ' data-add-label="' + (addLabel || '') + '">'
      + '<button type="button" class="product-card__step" data-card-qty="-1" aria-label="−">−</button>'
      + '<span class="product-card__step-val" data-card-qty-val>' + qty + '</span>'
      + '<button type="button" class="product-card__step" data-card-qty="1" aria-label="+"' + (atMax ? ' disabled' : '') + '>+</button>'
      + '</div>';
  }

  function addButtonHtml(variantId, label) {
    return '<button type="button" class="product-card__add" data-quick-add'
      + ' data-variant-id="' + variantId + '">' + label + '</button>';
  }

  function pdpButtonHtml(label) {
    return '<button type="submit" class="btn btn-primary product-form__submit" data-add-to-cart>'
      + label + '</button>';
  }

  /* Tope de inventario de una variante (data-product-inventory del PDP).
     null = sin tope (sin tracking o venta permitida sin stock). */
  function inventoryMax(form, variantId) {
    var wrapper = form.closest('[data-product-form-wrapper]') || document;
    var invEl = wrapper.querySelector('[data-product-inventory]');
    if (!invEl) return null;
    try { return (JSON.parse(invEl.textContent) || {})[variantId]; } catch (e) { return null; }
  }

  /* Vuelve del stepper al botón correcto según contexto (card o buy box) */
  function revertStepper(stepper, variantId, label) {
    stepper.outerHTML = stepper.hasAttribute('data-pdp')
      ? pdpButtonHtml(label)
      : addButtonHtml(variantId, label);
  }

  // Reemplaza un nodo por el stepper y devuelve el elemento insertado.
  function replaceWithStepper(node, variantId, qty, label) {
    var temp = document.createElement('div');
    temp.innerHTML = stepperHtml(variantId, qty, label);
    var el = temp.firstElementChild;
    node.replaceWith(el);
    return el;
  }

  /* Agrega 1 unidad. Solo red + refresh; la UI ya se actualizó optimista.
     Usa la respuesta de /cart/add.js (ya trae quantity) → sin /cart.js extra. */
  function cardAdd(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function(r) {
        if (!r.ok) return r.json().then(function(d) { throw new Error(d && d.description ? d.description : 'add failed'); });
        return r.json();
      })
      .then(function(data) {
        var line = data.items ? data.items[0] : data;
        var qty = line && line.quantity ? line.quantity : 1;
        return refreshMiniCart().then(function(count) {
          if (count !== null) updateCartBadge(count);
          if (window.ThemeUtils && ThemeUtils.announce && miniCart.dataset.tAdded) {
            ThemeUtils.announce(miniCart.dataset.tAdded);
          }
          return qty;
        });
      });
  }

  // Spinner en el botón mientras agrega; SOLO al confirmar abre el drawer y
  // muestra el stepper. Antes abría el drawer optimista y el producto entraba
  // un segundo después (se veía como que el click no hacía nada).
  function spinnerAdd(btn, variantId, label) {
    btn.classList.add('is-loading');
    btn.disabled = true;
    cardAdd(variantId)
      .then(function(qty) {
        replaceWithStepper(btn, variantId, qty || 1, label);
        openMiniCart();
      })
      .catch(function() {
        btn.classList.remove('is-loading'); // error → vuelve al botón
        btn.disabled = false;
      });
  }

  // Click en "Agregar al carrito"
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-quick-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    e.stopPropagation(); // la card es clickable: no propagar al link

    var variantId = btn.getAttribute('data-variant-id');
    var handle = btn.getAttribute('data-handle');
    var url = btn.getAttribute('data-url') || (handle ? '/products/' + handle : null);
    var label = btn.textContent.trim();

    if (variantId) {
      spinnerAdd(btn, variantId, label);
      return;
    }

    // Cards TS sin variante indexada → consultar el producto antes (no sabemos
    // si agrega o redirige), así que aquí no es optimista.
    btn.classList.add('is-loading');
    fetch('/products/' + handle + '.js')
      .then(function(r) { return r.json(); })
      .then(function(p) {
        btn.classList.remove('is-loading');
        if (p.variants && p.variants.length === 1 && p.variants[0].available) {
          spinnerAdd(btn, p.variants[0].id, label);
        } else if (url) {
          window.location.href = url; // varias variantes → elegir en PDP
        }
      })
      .catch(function() { btn.classList.remove('is-loading'); if (url) window.location.href = url; });
  });

  // Click en − / + del stepper (optimista)
  document.addEventListener('click', function(e) {
    var stepBtn = e.target.closest('[data-card-qty]');
    if (!stepBtn) return;
    e.preventDefault();
    e.stopPropagation();

    var stepper = stepBtn.closest('[data-card-stepper]');
    if (!stepper || stepper.classList.contains('is-busy')) return;
    if (stepBtn.disabled) return;
    var variantId = stepper.getAttribute('data-variant-id');
    var addLabel = stepper.getAttribute('data-add-label');
    var valEl = stepper.querySelector('[data-card-qty-val]');
    var plusBtn = stepper.querySelector('[data-card-qty="1"]');
    var max = stepper.dataset.max != null ? parseInt(stepper.dataset.max, 10) : null;
    var current = parseInt(valEl.textContent, 10) || 0;
    var target = current + parseInt(stepBtn.getAttribute('data-card-qty'), 10);

    // Tope de inventario (PDP): nunca pedir más que el stock disponible
    if (max != null && target > max) return;

    function syncPlus(n) { if (plusBtn && max != null) plusBtn.disabled = n >= max; }

    if (target >= 1) {
      valEl.textContent = target; // optimista
      syncPlus(target);
      changeCartQty(variantId, target)
        .then(function(cart) {
          var qty = lineQty(cart, variantId);
          if (qty !== target) valEl.textContent = qty; // servidor topó inventario
          syncPlus(parseInt(valEl.textContent, 10) || 0);
        })
        .catch(function() { valEl.textContent = current; syncPlus(current); }); // revierte
    } else {
      // 0 → quita la línea y vuelve al botón "Agregar"
      stepper.classList.add('is-busy');
      changeCartQty(variantId, 0)
        .then(function() { revertStepper(stepper, variantId, addLabel); })
        .catch(function() { stepper.classList.remove('is-busy'); });
    }
  });

  /* Cambio de cantidad de una línea sin re-render de toda la lista (evita el
     parpadeo). Reemplaza solo ese <item> + el footer con el HTML fresco de la
     Sections API; así el precio de línea y el tope de stock quedan al día. */
  function updateCartLine(key, quantity, itemEl, prevQty, valEl) {
    cartBusy = true;
    miniCart.classList.add('is-busy');
    return flushNote()
    .then(function() {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
    })
    .then(function(r) { if (!r.ok) throw new Error('qty'); return r.json(); })
    .then(function(cart) {
      updateCartBadge(cart.item_count);
      syncPageSteppers(cart); // refleja el cambio en el stepper del PDP
      syncCartPage();         // y en la lista de /cart si está abierta
      return refreshMiniCartItem(key);
    })
    .catch(function() {
      if (valEl) valEl.textContent = prevQty; // revierte el optimismo
    })
    .finally(function() {
      cartBusy = false;
      miniCart.classList.remove('is-busy');
    });
  }

  function refreshMiniCartItem(key) {
    return fetch('/?sections=cart-drawer')
      .then(function(r) { if (!r.ok) throw new Error('sec'); return r.json(); })
      .then(function(data) {
        var temp = document.createElement('div');
        temp.innerHTML = data['cart-drawer'];
        var sel = '[data-mini-cart-item][data-line-key="' + key + '"]';
        var fresh = temp.querySelector(sel);
        var old = miniCart.querySelector(sel);
        if (fresh && old) old.replaceWith(fresh);
        var footer = miniCart.querySelector('[data-mini-cart-footer]');
        var newFooter = temp.querySelector('[data-mini-cart-footer]');
        if (footer && newFooter) footer.innerHTML = newFooter.innerHTML;
        var count = miniCart.querySelector('[data-mini-cart-count]');
        var newCount = temp.querySelector('[data-section-cart-count]');
        if (count && newCount) count.textContent = newCount.textContent;
      });
  }

  // CWV-1: Uses Shopify Sections API instead of fetching the full page.
  // Returns the cart item count so callers don't need a separate /cart.js fetch.
  function refreshMiniCart() {
    return fetch('/?sections=cart-drawer')
      .then(function(r) {
        if (!r.ok) throw new Error('Mini-cart section fetch failed');
        return r.json();
      })
      .then(function(data) {
        var tempDiv = document.createElement('div');
        tempDiv.innerHTML = data['cart-drawer'];
        var body = miniCart.querySelector('[data-mini-cart-body]');
        var footer = miniCart.querySelector('[data-mini-cart-footer]');
        var count = miniCart.querySelector('[data-mini-cart-count]');
        var newBody = tempDiv.querySelector('[data-mini-cart-body]');
        var newFooter = tempDiv.querySelector('[data-mini-cart-footer]');
        var newCount = tempDiv.querySelector('[data-section-cart-count]');
        if (body && newBody) body.innerHTML = newBody.innerHTML;
        if (footer && newFooter) footer.innerHTML = newFooter.innerHTML;
        if (count && newCount) count.textContent = newCount.textContent;
        return newCount ? parseInt(newCount.textContent, 10) || 0 : 0;
      })
      .catch(function(error) {
        console.error('Mini-cart refresh error:', error);
        return null;
      });
  }

  function updateCartBadge(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function(el) {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  /* ============ Checkout: feedback al pulsar ============ */
  /* Los dos botones de "Finalizar compra" son submits nativos que navegan al
     checkout de Shopify. Eso tarda uno o dos segundos en los que no pasaba
     nada visible: el botón más importante de la tienda parecía no responder e
     invitaba a pulsarlo de nuevo. */
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-cart-checkout], [data-mc-checkout]');
    if (!btn || btn.disabled) return;
    btn.classList.add('is-loading');
    /* Si el navegador cancela la navegación (vuelta atrás, error de red), se
       devuelve el botón a su estado normal en vez de dejarlo girando. */
    setTimeout(function() { btn.classList.remove('is-loading'); }, 8000);
  });

  /* ============ Product form submit (add to cart) ============ */
  function showFormError(form, message) {
    var errorEl = form.querySelector('[data-form-error]');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function hideFormError(form) {
    var errorEl = form.querySelector('[data-form-error]');
    if (errorEl) errorEl.hidden = true;
  }

  document.querySelectorAll('form[data-product-form]').forEach(function(form) {
    form.addEventListener('submit', function(event) {
      event.preventDefault();

      var variantInput = form.querySelector('[data-variant-id]');
      var quantityInput = form.querySelector('[data-qty-input]');
      var submitButton = form.querySelector('[data-add-to-cart]');

      var variantId = variantInput && variantInput.value;
      var quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

      if (!variantId) return;

      hideFormError(form);

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.classList.add('is-loading'); // CWV-2: immediate visual feedback
      }

      window.addToCart(variantId, quantity)
        .then(function(qty) {
          /* Éxito: el botón se convierte en stepper (mismo patrón que las
             cards) para que la cantidad se pueda ajustar desde el buy box. */
          if (submitButton && submitButton.isConnected) {
            var label = form.dataset.addText || submitButton.textContent.trim();
            var max = inventoryMax(form, variantId);
            var temp = document.createElement('div');
            temp.innerHTML = stepperHtml(variantId, qty || quantity, label, true, max);
            submitButton.replaceWith(temp.firstElementChild);
          }
        })
        .catch(function(error) {
          showFormError(form, error && error.message ? error.message : form.dataset.errorText || 'Error');
        })
        .finally(function() {
          if (submitButton && submitButton.isConnected) {
            submitButton.disabled = false;
            submitButton.classList.remove('is-loading');
          }
        });
    });
  });

  /* Al cargar el PDP: si la variante ya está en el carrito (agregada desde otra
     página / mini-cart), mostrar el stepper con su cantidad en vez del botón
     "Agregar". Antes el PDP siempre pintaba el botón e ignoraba el carrito. */
  function initProductFormSteppers() {
    var forms = document.querySelectorAll('form[data-product-form]');
    if (!forms.length) return;
    fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        forms.forEach(function(form) {
          var variantInput = form.querySelector('[data-variant-id]');
          var submitButton = form.querySelector('[data-add-to-cart]');
          if (!variantInput || !submitButton || !submitButton.isConnected) return;
          var variantId = variantInput.value;
          var qty = lineQty(cart, variantId);
          if (qty <= 0) return;
          var label = form.dataset.addText || submitButton.textContent.trim();
          var temp = document.createElement('div');
          temp.innerHTML = stepperHtml(variantId, qty, label, true, inventoryMax(form, variantId));
          submitButton.replaceWith(temp.firstElementChild);
        });
      })
      .catch(function() {});
  }
  initProductFormSteppers();
})();
